import {
  Box,
  Typography,
  TablePagination,
  Divider,
  Checkbox,
  Stack,
  IconButton,
  Tooltip,
  type IconButtonProps,
  type Theme,
} from '@mui/material';
import type { RowSelectionInstance } from '@tanstack/react-table';
import classnames from 'classnames';
import React, { forwardRef } from 'react';

import { ActionsBar, ActionsToolBar } from './actionsBar';
import { PaginationTitle } from './paginationTitle';

interface PaginationBarProps<T extends object> {
  isLoading: boolean;
  entityLabel: string;
  selection?: {
    multiSelect?: {
      getIsAllRecordsSelected: RowSelectionInstance<T>['getIsAllRowsSelected'];
      getIsSomeRecordsSelected: RowSelectionInstance<T>['getIsSomeRowsSelected'];
      getToggleAllRecordsSelectedHandler: RowSelectionInstance<T>['getToggleAllRowsSelectedHandler'];
      ActionButton?: React.ReactNode;
    };
    selectedRecordsCount?: number;
  };
  pagination?: {
    pageIndex: number;
    pageSize: number;
    recordsCount?: number;
    recordsLimit?: number;
    reachedLastPage?: boolean;
  };
  onRowsPerPageChange: (pageIndex: number, pageSize: number) => void;
  onPageChange: (page: number) => void;
  actions?: React.ReactElement[];
  overridePadding?: {
    left?: number | string;
    right?: number | string;
  };
}

export function PaginationBar<T extends object>(props: PaginationBarProps<T>) {
  const {
    pagination,
    selection,
    onPageChange,
    onRowsPerPageChange,
    entityLabel,
    isLoading,
    actions,
    overridePadding,
  } = props;

  const { multiSelect } = selection ?? {};
  const MultiSelectActionButton = multiSelect?.ActionButton || null;
  const showFilteringActions = (actions || []).length > 0;
  const areSomeSelected = (selection?.selectedRecordsCount ?? 0) > 0;
  const recordsCount =
    pagination?.recordsCount === undefined ? -1 : pagination.recordsCount;

  const toCssLength = (v: number | string) =>
    typeof v === 'number' ? `${v}px` : v;

  const leftPadding = toCssLength(overridePadding?.left ?? 28);
  const rightPadding = toCssLength(overridePadding?.right ?? 16);

  return (
    <ActionsBar
      position='static'
      className='mb-0 py-[8px]'
      sx={{ pl: leftPadding, pr: rightPadding }}
    >
      <ActionsToolBar disableGutters>
        <Box
          className={classnames('h-full pr-[12px]', {
            'rounded-[4px]': areSomeSelected,
            'bg-[var(--mui-palette-background-paper)]': areSomeSelected,
          })}
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            height: '24px',
            boxShadow: areSomeSelected
              ? 'var(--mui-customShadows-panel)'
              : undefined,
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            height='52px'
            width='100%'
          >
            <Stack direction='row' alignItems='center'>
              {!!multiSelect && !isLoading && (
                <Checkbox
                  data-testid={'pagination-select-all'}
                  className={classnames('ml-[12px]')}
                  checked={multiSelect.getIsAllRecordsSelected() || false}
                  indeterminate={
                    multiSelect.getIsSomeRecordsSelected() &&
                    !multiSelect.getIsAllRecordsSelected()
                  }
                  onChange={multiSelect.getToggleAllRecordsSelectedHandler()}
                />
              )}
              <PaginationTitle
                entityLabel={entityLabel}
                selection={{
                  ...selection,
                  isAllSelected: multiSelect?.getIsAllRecordsSelected(),
                }}
                isLoading={isLoading}
                recordsCount={recordsCount}
              />
            </Stack>
            {areSomeSelected && MultiSelectActionButton}
          </Stack>
        </Box>
        <>
          {pagination && !areSomeSelected && (
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              rowsPerPage={pagination.pageSize}
              component='div'
              disabled={isLoading}
              count={recordsCount}
              page={pagination.pageIndex}
              onPageChange={(_, page) => {
                onPageChange?.(page);
              }}
              onRowsPerPageChange={(e) => {
                const pageSize = parseInt(e.target.value);
                const pagesCount = Math.ceil(recordsCount / pageSize);
                const pageIndex = Math.min(
                  pagination.pageIndex,
                  pagesCount - 1
                );
                onRowsPerPageChange(pageIndex, pageSize);
              }}
              slotProps={{
                select: {
                  sx: {
                    borderRadius: '4px',
                    marginTop: '2px',
                    marginRight: '17px',
                    color: 'var(--mui-palette-text-primary)',

                    '&:hover': {
                      backgroundColor: 'var(--mui-palette-primary-dark)',
                    },
                  },
                  inputProps: {
                    'aria-label': 'Items per page',

                    sx: {
                      '&:focus': {
                        backgroundColor: 'transparent',
                      },
                    },
                  },
                  IconComponent: () => (
                    <i className='material-symbols-arrow-drop-down-rounded' />
                  ),

                  MenuProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        borderRadius: 0,
                      },
                    },
                  },
                },
                actions: {
                  previousButton: IconButtonPropsFactory('Previous page'),
                  nextButton: IconButtonPropsFactory(
                    'Next page',
                    pagination.reachedLastPage
                  ),
                },
              }}
              labelDisplayedRows={({ from, to, count }) => (
                <span className='flex items-center'>
                  <Divider
                    component='span'
                    orientation='vertical'
                    className='mr-[25px] h-[22px] inline-flex'
                  />
                  {count === -1 ? (
                    <Typography component='span'>{`Page ${pagination.pageIndex + 1}`}</Typography>
                  ) : (
                    <Typography component='span'>{`${from}–${to} of ${count === pagination.recordsLimit ? `more than ${count.toLocaleString()}` : count.toLocaleString()}`}</Typography>
                  )}
                </span>
              )}
              labelRowsPerPage={
                <Typography variant='subtitle1' component='span'>
                  Items per page
                </Typography>
              }
            />
          )}
          {showFilteringActions && (
            <>
              {areSomeSelected ? (
                <div className='my-[15px] mr-[16px]' />
              ) : (
                <Divider
                  orientation='vertical'
                  flexItem
                  className='my-[15px] mr-[16px]'
                />
              )}
              <Stack
                paddingRight='12px'
                direction='row'
                alignItems='center'
                gap='16px'
              >
                {actions}
              </Stack>
            </>
          )}
        </>
      </ActionsToolBar>
    </ActionsBar>
  );
}

const IconButtonPropsFactory = (tooltip: string, disabled?: boolean) => {
  const Button = forwardRef<HTMLButtonElement>(
    ({ title, ...props }: IconButtonProps, ref) => (
      <Tooltip title={tooltip}>
        <span>
          <IconButton
            {...props}
            disabled={disabled}
            ref={ref}
            data-testid={`${tooltip.toLowerCase()}-button`}
            disableRipple
          />
        </span>
      </Tooltip>
    )
  );

  return {
    component: Button,
    disableRipple: true,

    sx: (theme: Theme) => ({
      borderRadius: '4px',
      padding: '6px',

      '&:hover': {
        backgroundColor: theme.palette.background.primary,
      },

      svg: {
        width: '20px',
        height: '20px',
      },
    }),
  } satisfies IconButtonProps;
};
